import { useState, useRef, useEffect, useCallback } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  addDoc, 
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { trackEvent, getPatternFeatures, getTimeSignaturesUsed } from '../utils/analytics';

/**
 * Custom hook for handling autosave functionality
 * @param {Object} params - All the parameters needed for autosaving
 * @returns {Object} - Autosave state and functions
 */
export function useAutosave({
    // User and app state
    currentUser,
    isPlaying,
    subscriptionTier,
    
    // Pattern data
    measures,
    tempo,
    patternName,
    folderName,
    useTempoChanges,
    enableAdditionalTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    
    // App constants and utilities
    INITIAL_TEMPO,
    savedPatterns,
    setSavedPatterns,
    patternCache,
    removeUndefinedDeep,
    
    // Callback functions
    setPatternName,
    setFolderName,
    handleSavePattern,
    
    // Configuration
    autoSaveDelay = 120000,
    showToastNotifications = true,
    savedNotificationDuration = 10000,
  }) {
  // Autosave state
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);
  const [hasPromptedForName, setHasPromptedForName] = useState(false);
  
  // New states for modal-based autosave
  const [showAutosaveModal, setShowAutosaveModal] = useState(false);
  const [pendingAutosave, setPendingAutosave] = useState(null);
  
  // Timeout refs
  const autoSaveTimeoutRef = useRef(null);
  const savedNotificationTimeoutRef = useRef(null);

  // Function to show toast notifications
  const showToast = useCallback((message, type = 'info') => {
    if (showToastNotifications) {
      setToastMessage({ message, type, id: Date.now() });
    }
  }, [showToastNotifications]);

  // Reset the prompt flag when pattern name changes (e.g., when loading a new pattern)
  useEffect(() => {
    if (patternName.trim()) {
      setHasPromptedForName(false);
    }
  }, [patternName]);

  // Effect to auto-hide the "Saved" notification after 10 seconds
  useEffect(() => {
    // Clear any existing timeout
    if (savedNotificationTimeoutRef.current) {
      clearTimeout(savedNotificationTimeoutRef.current);
    }
    
    // If we have a lastAutoSave timestamp, set up timeout to clear it
    if (lastAutoSave) {
      savedNotificationTimeoutRef.current = setTimeout(() => {
        setLastAutoSave(null);
      }, savedNotificationDuration);
    }
    
    // Cleanup function
    return () => {
      if (savedNotificationTimeoutRef.current) {
        clearTimeout(savedNotificationTimeoutRef.current);
      }
    };
  }, [lastAutoSave, savedNotificationDuration]);

  // Function to handle autosave modal confirmation
  const handleAutosaveModalConfirm = useCallback(async (newPatternName, newFolderName) => {
    if (!pendingAutosave) return;
    
    try {
      // Update the pattern and folder names
      setPatternName(newPatternName);
      setFolderName(newFolderName);
      setHasPromptedForName(true);
      
      // Close the modal
      setShowAutosaveModal(false);
      
      // Perform the autosave with the new names
      showToast('Saving your pattern...', 'info');
      
      await handleSavePattern({
        isAutosave: true,
        showToast,
        hasPromptedForName: true,
        setHasPromptedForName,
        setPatternName,
        // Override the pattern name for this save
        overridePatternName: newPatternName,
        overrideFolderName: newFolderName
      });
      
      setLastAutoSave(new Date());
      
    } catch (error) {
      console.error("Autosave failed:", error);
      showToast('Failed to save pattern. Please check your connection.', 'error');
    } finally {
      setIsAutoSaving(false);
      setPendingAutosave(null);
    }
  }, [pendingAutosave, setPatternName, setFolderName, handleSavePattern, showToast, setHasPromptedForName]);

  // Function to handle autosave modal cancellation
  const handleAutosaveModalCancel = useCallback(() => {
    setShowAutosaveModal(false);
    setPendingAutosave(null);
    setIsAutoSaving(false);
    setHasPromptedForName(true); // Mark as prompted to avoid asking again immediately
    showToast('Autosave cancelled. Your work is still in progress.', 'info');
  }, [showToast]);

  // The main autosave function
  const performAutosave = useCallback(async () => {
    // Don't autosave if user is not logged in or disabled
    if (!currentUser || !autoSaveEnabled) return;
    
    // Don't autosave while playing
    if (isPlaying) return;
    
    // Don't autosave if there's no meaningful content
    if (measures.length === 1 && 
        measures[0].numerator === 4 && 
        measures[0].denominator === 4 && 
        tempo === INITIAL_TEMPO && 
        !patternName.trim()) {
      return;
    }

    // Don't autosave if modal is already showing
    if (showAutosaveModal) return;

    try {
      setIsAutoSaving(true);
      
      // If pattern has no name and we haven't prompted yet, show the modal
      if (!patternName.trim() && !hasPromptedForName) {
        showToast('🎵 Please name your pattern to save it!', 'info');
        setPendingAutosave({ timestamp: Date.now() });
        setShowAutosaveModal(true);
        return; // Don't continue with autosave, wait for modal
      }
      
      // If pattern has a name or we've already prompted, proceed with autosave
      showToast('Saving your pattern...', 'info');
      
      await handleSavePattern({
        isAutosave: true,
        showToast,
        hasPromptedForName,
        setHasPromptedForName,
        setPatternName
      });
      
      setLastAutoSave(new Date());
      
    } catch (error) {
      console.error("Autosave failed:", error);
      showToast('Failed to save pattern. Please check your connection.', 'error');
    } finally {
      // Only clear isAutoSaving if we're not waiting for modal
      if (!showAutosaveModal) {
        setIsAutoSaving(false);
      }
    }
  }, [
    currentUser,
    autoSaveEnabled,
    isPlaying,
    measures,
    tempo,
    patternName,
    INITIAL_TEMPO,
    hasPromptedForName,
    showAutosaveModal,
    handleSavePattern,
    showToast,
    setHasPromptedForName,
    setPatternName
  ]);

  // Effect to handle debounced autosave
  useEffect(() => {
    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    // Set up new autosave timeout
    autoSaveTimeoutRef.current = setTimeout(() => {
      performAutosave();
    }, autoSaveDelay);
    
    // Cleanup function
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [
    measures,
    tempo,
    useTempoChanges,
    tempoChanges,
    aTuning,
    muteRandomBeats,
    mutePercentage,
    loopMode,
    startMeasureNumber,
    stressOn,
    selectedNoteValue,
    modulateOnLoop,
    modLoopInterval,
    modLoopDirection,
    rehearsalLetters,
    timeSignatureColors,
    patternName,
    folderName,
    performAutosave,
    autoSaveDelay
  ]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (savedNotificationTimeoutRef.current) {
        clearTimeout(savedNotificationTimeoutRef.current);
      }
    };
  }, []);

  // Manual trigger function for immediate save
  const triggerAutosave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    performAutosave();
  }, [performAutosave]);

  return {
    // State
    isAutoSaving,
    lastAutoSave,
    autoSaveEnabled,
    setAutoSaveEnabled,
    toastMessage,
    setToastMessage,
    
    // New modal states
    showAutosaveModal,
    pendingAutosave,
    
    // Functions
    performAutosave,
    triggerAutosave,
    showToast,
    handleAutosaveModalConfirm,
    handleAutosaveModalCancel,
  };
}

export default useAutosave;